import React, { useEffect, useState } from "react";

import { createPortal } from "react-dom";

import {
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaSearchMinus,
  FaSearchPlus,
  FaTimes,
} from "react-icons/fa";

import { resolveTutorialMediaUrl } from "../utils/tutorialMediaUtils";

export default function TutorialLightbox({
  isOpen,
  currentMedia,
  currentIndex,
  total,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  onClose,
}) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setZoom(1);
  }, [currentMedia?.id, currentMedia?.url]);

  if (!isOpen || !currentMedia || typeof document === "undefined") {
    return null;
  }

  const imageUrl = resolveTutorialMediaUrl(currentMedia.url);

  const decreaseZoom = () => {
    setZoom((current) => Math.max(current - 0.25, 1));
  };

  const increaseZoom = () => {
    setZoom((current) => Math.min(current + 0.25, 2.5));
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1600] flex flex-col bg-slate-950/95 text-white backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Agrandissement de l’image"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        aria-label="Fermer"
      />

      <header className="relative z-10 flex items-center gap-3 border-b border-white/10 bg-slate-950/70 px-3 py-3 sm:px-5">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {currentMedia.alt || "Illustration du tutoriel"}
          </div>

          {total > 1 ? (
            <div className="mt-0.5 text-xs text-white/50">
              Image {currentIndex + 1} sur {total}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={zoom <= 1}
            onClick={decreaseZoom}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition hover:bg-white/20 disabled:opacity-30"
            aria-label="Réduire"
          >
            <FaSearchMinus />
          </button>

          <span className="hidden min-w-12 text-center text-xs text-white/70 sm:block">
            {Math.round(zoom * 100)} %
          </span>

          <button
            type="button"
            disabled={zoom >= 2.5}
            onClick={increaseZoom}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition hover:bg-white/20 disabled:opacity-30"
            aria-label="Agrandir"
          >
            <FaSearchPlus />
          </button>

          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition hover:bg-white/20"
            aria-label="Ouvrir l’image"
          >
            <FaExternalLinkAlt />
          </a>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-950 transition hover:bg-slate-200"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>
      </header>

      <div className="relative z-10 flex min-h-0 flex-1 items-center">
        {total > 1 ? (
          <button
            type="button"
            disabled={!hasPrevious}
            onClick={onPrevious}
            className="absolute left-2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 disabled:opacity-20 sm:left-5 sm:h-13 sm:w-13"
            aria-label="Image précédente"
          >
            <FaChevronLeft />
          </button>
        ) : null}

        <div className="flex h-full w-full items-center justify-center overflow-auto p-4 sm:p-10">
          <img
            src={imageUrl}
            alt={currentMedia.alt || "Illustration du tutoriel"}
            className="h-auto rounded-xl object-contain shadow-2xl transition-all duration-200"
            style={{
              width: zoom === 1 ? "auto" : `${zoom * 100}%`,
              maxWidth: zoom === 1 ? "100%" : "none",
              maxHeight: zoom === 1 ? "100%" : "none",
            }}
          />
        </div>

        {total > 1 ? (
          <button
            type="button"
            disabled={!hasNext}
            onClick={onNext}
            className="absolute right-2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 disabled:opacity-20 sm:right-5 sm:h-[52px] sm:w-[52px]"
            aria-label="Image suivante"
          >
            <FaChevronRight />
          </button>
        ) : null}
      </div>

      {currentMedia.caption ? (
        <footer className="relative z-10 border-t border-white/10 bg-slate-950/70 px-5 py-3 text-center text-xs text-white/65">
          {currentMedia.caption}
        </footer>
      ) : null}
    </div>,
    document.body,
  );
}
